import { Injectable } from '@angular/core';
import { Observable, timer, exhaustMap, takeWhile, map, catchError, of } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { IOperation } from 'src/app/user-identification/models/user-identification';
import { environment } from 'src/environments/environment';

export type EudiPresentationStatus = 'pending' | 'request_retrieved' | 'completed' | 'failed';

export interface EudiInitResponse {
	transaction_id: string;
	authorization_request_uri: string;
	client_id?: string | null;
	request_uri?: string | null;
}

export interface EudiStatusResponse {
	status: EudiPresentationStatus;
	feedback?: {
		level: string;
		title: string;
		message: string;
	};
}

/** Misma forma que IMiDNIValidateResponse / ResponseOkKo. */
export interface EudiValidateResponse {
	result: 'OK' | 'KO';
	error?: string;
	process?: IOperation[];
	process_uuid?: string;
}

/**
 * API EUDI journey — mismos nombres/estructura que MiDNI:
 * - prefix `/eudi` (como `/midni`)
 * - acciones kebab: init / status / validate (como validate-qr)
 */
@Injectable({
	providedIn: 'root',
})
export class EudiApiService extends ApiService {
	private readonly baseUrl = `${environment.identity2}/eudi`;

	public init(body: { claims?: string[]; same_device?: boolean } = {}): Observable<EudiInitResponse> {
		return this.http.post<EudiInitResponse>(`${this.baseUrl}/init`, body, {
			headers: this.getHeaders(),
		});
	}

	public getStatus(transactionId: string): Observable<EudiStatusResponse> {
		return this.http.get<EudiStatusResponse>(
			`${this.baseUrl}/status/${encodeURIComponent(transactionId)}`,
			{ headers: this.getHeaders() },
		);
	}

	/** Equivalente a MiDNI `validateQr`. */
	public validate(transactionId: string): Observable<EudiValidateResponse> {
		return this.http.post<EudiValidateResponse>(
			`${this.baseUrl}/validate`,
			{ transaction_id: transactionId },
			{ headers: this.getHeaders() },
		);
	}

	/**
	 * Poll hasta completed/failed o hasta `maxSessionSeconds` (tiempo máximo OCR del back).
	 * Default 150s = `side_max_time` típico; el front no debe superar ese techo.
	 *
	 * Usa `exhaustMap` (no `switchMap`): si el Verifier tarda > interval, switchMap
	 * cancelaba el GET que traía `completed` y el journey se quedaba en el QR.
	 */
	public pollUntilDone(
		transactionId: string,
		maxSessionSeconds = 150,
		intervalMs = 2000,
	): Observable<EudiStatusResponse> {
		const safeInterval = Math.max(500, intervalMs);
		const maxAttempts = Math.max(1, Math.ceil(maxSessionSeconds / (safeInterval / 1000)));
		let attempts = 0;

		return timer(0, safeInterval).pipe(
			exhaustMap(() => {
				attempts += 1;
				return this.getStatus(transactionId).pipe(
					catchError(() =>
						of({
							status: 'failed' as const,
							feedback: {
								level: 'error',
								title: 'error',
								message: 'status_request_failed',
							},
						}),
					),
					map((res) => {
						if (
							attempts >= maxAttempts &&
							res.status !== 'completed' &&
							res.status !== 'failed'
						) {
							return {
								status: 'failed' as const,
								feedback: {
									level: 'error',
									title: 'timeout',
									message: 'poll_timeout',
								},
							};
						}
						return res;
					}),
				);
			}),
			takeWhile((res) => res.status !== 'completed' && res.status !== 'failed', true),
		);
	}
}
